/**
 * src/context/ThemeContext.tsx
 * Theme switcher context managing Obsidian Dark & Crystal Light modes
 */

import React, { createContext, useContext, useEffect, useState } from 'react';

export type Theme = 'dark' | 'light';
export type LogoStyle = 'auto' | 'dark' | 'light';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  logoStyle: LogoStyle;
  setLogoStyle: (style: LogoStyle) => void;
  activeLogo: string;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    const saved = localStorage.getItem('ckaudit_theme') as Theme;
    if (saved === 'dark' || saved === 'light') return saved;
    return 'dark'; // Default Obsidian Luxury
  });

  const [logoStyle, setLogoStyleState] = useState<LogoStyle>(() => {
    const saved = localStorage.getItem('ckaudit_logo_style') as LogoStyle;
    if (saved === 'auto' || saved === 'dark' || saved === 'light') return saved;
    return 'auto';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('ckaudit_theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setThemeState(prev => (prev === 'dark' ? 'light' : 'dark'));
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const setLogoStyle = (style: LogoStyle) => {
    setLogoStyleState(style);
    localStorage.setItem('ckaudit_logo_style', style);
  };

  // Determine active logo file based on user selection or auto theme sync
  const activeLogo = logoStyle === 'dark'
    ? '/logos/logo-dark.png'
    : logoStyle === 'light'
    ? '/logos/logo-light.png'
    : (theme === 'dark' ? '/logos/logo-dark.png' : '/logos/logo-light.png');

  // Dynamically synchronize PWA manifest icon, browser favicon, and Apple Touch Icon
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // 1. Browser Favicon
    const favicon = document.querySelector<HTMLLinkElement>("link[rel*='icon']");
    if (favicon) {
      favicon.href = activeLogo;
    }

    // 2. iOS Safari Apple Touch Icon
    const appleTouchIcon = document.querySelector<HTMLLinkElement>("link[rel='apple-touch-icon']");
    if (appleTouchIcon) {
      appleTouchIcon.href = activeLogo;
    }

    // 3. Dynamic PWA Web Manifest with user selected logo as primary
    const manifestLink = document.querySelector<HTMLLinkElement>("link[rel='manifest']");
    if (manifestLink) {
      try {
        const dynamicManifest = {
          name: "CKAudit — Credit & Debit Ledger",
          short_name: "CKAudit",
          description: "Mobile-first luxury credit & debit ledger with Admin approval workflow and real-time smart settlement.",
          start_url: "/",
          display: "standalone",
          orientation: "portrait-primary",
          background_color: theme === 'dark' ? "#080b12" : "#f8fafc",
          theme_color: theme === 'dark' ? "#080b12" : "#f8fafc",
          icons: [
            {
              src: activeLogo,
              sizes: "192x192 512x512 1024x1024",
              type: "image/png",
              purpose: "any maskable"
            },
            {
              src: activeLogo === '/logos/logo-dark.png' ? '/logos/logo-light.png' : '/logos/logo-dark.png',
              sizes: "192x192 512x512 1024x1024",
              type: "image/png",
              purpose: "any maskable"
            }
          ]
        };
        const blob = new Blob([JSON.stringify(dynamicManifest, null, 2)], { type: 'application/manifest+json' });
        manifestLink.href = URL.createObjectURL(blob);
      } catch (err) {
        console.warn('Dynamic manifest update skipped:', err);
      }
    }
  }, [activeLogo, theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, logoStyle, setLogoStyle, activeLogo }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
